import { useEffect } from "react";
import { RotateCcw, Star, X } from "lucide-react";
import CATEGORY_OPTIONS_SEARCH from "../../../constants/categoryOptionsSearch";
import "./mapFilters.css";

export const DEFAULT_MAP_FILTERS = {
	query: "",
	category: "all",
	subCategory: "all",
	location: "all",
	minRating: 0,
};

const RATING_STEPS = [3, 4, 4.5];

const serviceLocation = (service) => service.locationType || (service.isHomeService ? "home" : "in_shop");

const servicesOf = (shop) => (Array.isArray(shop?.services) ? shop.services : []);

const categoryLabel = (value, language) => {
	const parent = CATEGORY_OPTIONS_SEARCH.find((c) => c.value === value);
	if (!parent) return value;

	return language === "es" ? parent.labelES : parent.labelEN;
};

const subCategoryLabel = (category, value, language) => {
	const parent = CATEGORY_OPTIONS_SEARCH.find((c) => c.value === category);
	const sub = parent?.subCategories?.find((s) => s.value === value);
	if (!sub) return value;

	return language === "es" ? sub.labelES : sub.labelEN;
};

/* A sub that repeats its parent — or is the catch-all "all" — narrows nothing,
   so it never earns a chip. */
const isRealSubCategory = (category, sub) => Boolean(sub) && sub !== "all" && sub !== category;

/* ── FACETS ──
   Built from the shops currently in view, so the sheet never offers a category
   or a specialty that would come back empty. */
export const buildFacets = (shops, language) => {
	const categories = new Map();
	const subCategories = new Map();
	const locations = new Set();

	shops.forEach((shop) => {
		if (shop.category) {
			categories.set(shop.category, (categories.get(shop.category) || 0) + 1);

			if (isRealSubCategory(shop.category, shop.subCategory)) {
				if (!subCategories.has(shop.category)) subCategories.set(shop.category, new Map());

				const bucket = subCategories.get(shop.category);
				bucket.set(shop.subCategory, (bucket.get(shop.subCategory) || 0) + 1);
			}
		}

		servicesOf(shop).forEach((service) => locations.add(serviceLocation(service)));
	});

	const byCount = (a, b) => b.count - a.count || a.label.localeCompare(b.label);

	return {
		categories: [...categories.entries()]
			.map(([value, count]) => ({ value, count, label: categoryLabel(value, language) }))
			.sort(byCount),

		/* Keyed by parent, so the sheet can show only the selected one's */
		subCategories: Object.fromEntries(
			[...subCategories.entries()].map(([category, bucket]) => [
				category,
				[...bucket.entries()]
					.map(([value, count]) => ({ value, count, label: subCategoryLabel(category, value, language) }))
					.sort(byCount),
			])
		),

		locations: ["in_shop", "home", "online"].filter((key) => locations.has(key)),
	};
};

/* ── MATCHING ──
   Returns the shop decorated with `matchedServices` (the rows worth showing on
   its card), or null when it drops out. `matchedServices: null` means nothing
   narrowed the service list, so the card shows its usual first few. */
export const matchShop = (shop, filters) => {
	const query = (filters.query || "").trim().toLowerCase();
	const services = servicesOf(shop);

	if (filters.category !== "all" && shop.category !== filters.category) return null;
	if (filters.subCategory !== "all" && shop.subCategory !== filters.subCategory) return null;
	if (filters.minRating > 0 && Number(shop.rating || 0) < filters.minRating) return null;

	const wantsLocation = filters.location !== "all";

	/* The pool every later test draws from: services clearing the type filter. */
	const pool = wantsLocation ? services.filter((service) => serviceLocation(service) === filters.location) : services;

	if (wantsLocation && pool.length === 0) return null;

	if (!query) return { ...shop, matchedServices: wantsLocation ? pool : null };

	/* A service hit is the more useful answer, so it wins over the shop name. */
	const serviceHits = pool.filter((service) => typeof service.name === "string" && service.name.toLowerCase().includes(query));
	if (serviceHits.length > 0) return { ...shop, matchedServices: serviceHits };

	const nameHit = [shop.name, shop.address, shop.city].some(
		(field) => typeof field === "string" && field.toLowerCase().includes(query)
	);
	if (!nameHit) return null;

	return { ...shop, matchedServices: wantsLocation ? pool : null };
};

export const filterShops = (shops, filters) => shops.map((shop) => matchShop(shop, filters)).filter(Boolean);

export const countActiveFilters = (filters) =>
	((filters.query || "").trim() ? 1 : 0) +
	(filters.category !== "all" ? 1 : 0) +
	(filters.subCategory !== "all" ? 1 : 0) +
	(filters.location !== "all" ? 1 : 0) +
	(filters.minRating > 0 ? 1 : 0);

/* ── SHEET ── */
const FilterChip = ({ active, onClick, children }) => (
	<button type="button" className={`sf-mf-chip${active ? " is-active" : ""}`} aria-pressed={active} onClick={onClick}>
		{children}
	</button>
);

const MapFilters = ({ open, onClose, filters, onChange, onReset, facets, resultCount, copy }) => {
	useEffect(() => {
		if (!open) return undefined;

		const onKey = (event) => {
			if (event.key === "Escape") onClose();
		};

		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open) return null;

	const activeCount = countActiveFilters(filters);
	/* Specialties only make sense under one parent, so they wait for a pick */
	const subCategories = filters.category === "all" ? [] : facets.subCategories[filters.category] || [];

	return (
		<div className="sf-mf-layer" role="dialog" aria-modal="true" aria-label={copy.title}>
			<button type="button" className="sf-mf-scrim" aria-label={copy.close} onClick={onClose} />

			<div className="sf-mf-sheet">
				<header className="sf-mf-head">
					<span className="sf-mf-grab" aria-hidden="true" />

					<div className="sf-mf-head-row">
						<div>
							<h2 className="sf-mf-title">{copy.title}</h2>
							<p className="sf-mf-subtitle">{copy.subtitle}</p>
						</div>

						<button type="button" className="sf-mf-close" aria-label={copy.close} onClick={onClose}>
							<X size={18} strokeWidth={2.2} />
						</button>
					</div>
				</header>

				<div className="sf-mf-body">
					{facets.categories.length > 1 && (
						<section className="sf-mf-group">
							<h3 className="sf-mf-group-title">{copy.category}</h3>

							<div className="sf-mf-chips">
								<FilterChip
									active={filters.category === "all"}
									onClick={() => onChange({ category: "all", subCategory: "all" })}
								>
									{copy.all}
								</FilterChip>

								{facets.categories.map((category) => (
									<FilterChip
										key={category.value}
										active={filters.category === category.value}
										/* A stale specialty from the old parent would zero the results */
										onClick={() => onChange({ category: category.value, subCategory: "all" })}
									>
										{category.label}
										<i>{category.count}</i>
									</FilterChip>
								))}
							</div>
						</section>
					)}

					{subCategories.length > 0 && (
						<section className="sf-mf-group">
							<h3 className="sf-mf-group-title">{copy.subCategory}</h3>

							<div className="sf-mf-chips">
								<FilterChip active={filters.subCategory === "all"} onClick={() => onChange({ subCategory: "all" })}>
									{copy.all}
								</FilterChip>

								{subCategories.map((sub) => (
									<FilterChip
										key={sub.value}
										active={filters.subCategory === sub.value}
										onClick={() => onChange({ subCategory: sub.value })}
									>
										{sub.label}
										<i>{sub.count}</i>
									</FilterChip>
								))}
							</div>
						</section>
					)}

					{facets.locations.length > 1 && (
						<section className="sf-mf-group">
							<h3 className="sf-mf-group-title">{copy.serviceType}</h3>

							<div className="sf-mf-chips">
								<FilterChip active={filters.location === "all"} onClick={() => onChange({ location: "all" })}>
									{copy.any}
								</FilterChip>

								{facets.locations.map((key) => (
									<FilterChip key={key} active={filters.location === key} onClick={() => onChange({ location: key })}>
										{copy.serviceTypes[key]}
									</FilterChip>
								))}
							</div>
						</section>
					)}

					<section className="sf-mf-group">
						<h3 className="sf-mf-group-title">{copy.rating}</h3>

						<div className="sf-mf-chips">
							<FilterChip active={filters.minRating === 0} onClick={() => onChange({ minRating: 0 })}>
								{copy.any}
							</FilterChip>

							{RATING_STEPS.map((value) => (
								<FilterChip key={value} active={filters.minRating === value} onClick={() => onChange({ minRating: value })}>
									<Star size={12} fill="currentColor" strokeWidth={0} className="sf-mf-star" />
									{copy.ratingOption(value)}
								</FilterChip>
							))}
						</div>
					</section>
				</div>

				<footer className="sf-mf-foot">
					<button type="button" className="sf-mf-reset" onClick={onReset} disabled={activeCount === 0}>
						<RotateCcw size={14} strokeWidth={2.2} />
						{copy.reset}
					</button>

					<button type="button" className="sf-mf-apply" onClick={onClose}>
						{resultCount === 0 ? copy.noResults : copy.apply(resultCount)}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default MapFilters;
